import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, BucketBadge } from "@/components/ui-bits";
import { fmtINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActivityLoggerButton } from "@/components/ActivityLogger";
import { toast } from "sonner";
import { Warning, Fire, ArrowRight, Brain, Lightning } from "@phosphor-icons/react";

const scoreColor = (s) => s >= 70 ? "bg-red-100 text-red-800 border-red-300" : s >= 45 ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-blue-100 text-blue-800 border-blue-300";
const scoreLabel = (s) => s >= 70 ? "Critical" : s >= 45 ? "High" : "Normal";

export default function WorkQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], broken_ptp_count: 0, total: 0, unscored: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [sort, setSort] = useState("priority");
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);
  const canFilter = ["super_admin", "client_admin", "manager"].includes(user.role);

  useEffect(() => { if (canFilter) api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, [canFilter]);

  const load = () => {
    setLoading(true);
    const params = { sort };
    if (agentId) params.agent_id = agentId;
    api.get("/work-queue", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [sort, agentId]);

  const processBroken = async () => {
    setProcessing(true);
    try {
      const r = await api.post("/work-queue/process-broken-ptps");
      toast.success(r.data.created > 0 ? `${r.data.created} follow-up(s) created for broken PTPs` : "No new broken PTPs to action");
      load();
    } catch (e) { toast.error("Failed to process broken PTPs"); }
    finally { setProcessing(false); }
  };

  const scoreTop = async () => {
    setScoring(true);
    try {
      const r = await api.post("/work-queue/score-top", { limit: 10 }, { params: agentId ? { agent_id: agentId } : {} });
      toast.success(r.data.scored > 0 ? `AI-scored ${r.data.scored} accounts` : "All top accounts recently scored (cool-down active)");
      if (sort !== "ai") setSort("ai"); else load();
    } catch (e) { toast.error("AI scoring failed"); }
    finally { setScoring(false); }
  };

  return (
    <div data-testid="work-queue-page">
      <PageHeader title="Smart Work Queue" subtitle={`${data.total} accounts prioritized by likelihood-to-recover`}>
        <div className="flex flex-wrap items-center gap-2">
          {canFilter && (
            <Select value={agentId || "all"} onValueChange={(v) => setAgentId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 rounded-sm h-9" data-testid="wq-agent-filter"><SelectValue placeholder="All agents" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-sm h-9" data-testid="wq-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="priority">Sort: Priority</SelectItem>
              <SelectItem value="ai">Sort: AI Score</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-sm gap-2 h-9" onClick={scoreTop} disabled={scoring} data-testid="wq-score-top">
            <Brain size={15} weight="bold" /> {scoring ? "Scoring…" : "AI-score top 10"}
          </Button>
        </div>
      </PageHeader>

      {sort === "ai" && data.unscored > 0 && (
        <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5" data-testid="wq-unscored-note">
          <Lightning size={13} weight="bold" /> {data.unscored} of {data.total} accounts not yet AI-scored — use "AI-score top 10" to rank the most urgent.
        </div>
      )}

      {data.broken_ptp_count > 0 && (
        <div className="border border-red-300 bg-red-50 p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="broken-ptp-alert">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-red-100 border border-red-300 flex items-center justify-center shrink-0">
              <Warning size={20} weight="bold" className="text-red-700" />
            </div>
            <div>
              <div className="font-heading font-bold text-red-800">{data.broken_ptp_count} Broken Promise{data.broken_ptp_count > 1 ? "s" : ""} to Pay</div>
              <div className="text-sm text-red-700">Borrowers who missed their promised payment date — re-engage these first.</div>
            </div>
          </div>
          <Button onClick={processBroken} disabled={processing} className="rounded-sm gap-2 bg-red-700 hover:bg-red-800 shrink-0" data-testid="process-broken-ptps">
            <Fire size={16} weight="bold" /> {processing ? "Creating…" : "Create Follow-ups"}
          </Button>
        </div>
      )}

      <div className="border border-border bg-card">
        {loading && <div className="label-caps p-6 text-center">Loading…</div>}
        {!loading && data.items.length === 0 && <Empty text="Queue is clear. Great job!" />}
        {!loading && data.items.map((it, idx) => (
          <div key={it.account_id} className="flex items-center justify-between gap-4 p-4 border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`wq-item-${it.account_id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <span className="text-xs mono text-muted-foreground w-6 shrink-0 text-right">{idx + 1}</span>
              <div className={`flex flex-col items-center justify-center h-12 w-12 rounded-sm border shrink-0 ${scoreColor(it.priority_score)}`} title={`Priority ${it.priority_score}/100`}>
                <span className="text-base font-black font-heading leading-none mono">{it.priority_score}</span>
                <span className="text-[9px] font-bold uppercase tracking-wide">{scoreLabel(it.priority_score)}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{it.borrower_name}</span>
                  <span className="mono text-xs text-muted-foreground">{it.loan_id}</span>
                  <BucketBadge bucket={it.bucket} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className="text-xs text-muted-foreground mono mr-1">{fmtINR(it.outstanding_amount)} • {it.dpd}d DPD</span>
                  {it.ai_score != null && (
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-sm ${it.ai_band === "High" ? "bg-emerald-100 text-emerald-700" : it.ai_band === "Medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`} title="AI likelihood to pay">AI {it.ai_score}</span>
                  )}
                  {it.reasons.map((r, i) => (
                    <span key={i} className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-sm ${r.startsWith("Broken PTP") ? "bg-red-100 text-red-700" : r.includes("overdue") ? "bg-amber-100 text-amber-700" : r === "Never contacted" ? "bg-orange-100 text-orange-700" : "bg-secondary text-muted-foreground"}`}>{r}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ActivityLoggerButton accountId={it.account_id} borrowerName={it.borrower_name} variant="icon" testIdSuffix={`wq-${it.account_id}`} onSaved={load} />
              <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => navigate(`/accounts/${it.account_id}`)} data-testid={`wq-open-${it.account_id}`}>
                Open <ArrowRight size={14} weight="bold" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
